import { apiClient } from "./client";
import type {
  ConversationSignal,
  ConversationStrategy,
  ConversationCatalogListResponse,
  ConversationStrategyListResponse,
  SignalUpsertPayload,
  StrategyUpsertPayload,
} from "../types/conversationSignals";

// The backend wraps list responses as { output: { items: [...], next_page_token, ... } }

async function listSignals(params?: {
  search?: string;
  is_active?: boolean;
  page_token?: string;
  max_results?: number;
  scope_type?: "stream" | "user";
  scope_uuid?: string;
}): Promise<ConversationCatalogListResponse> {
  const { data } = await apiClient.get<{
    output: {
      items: ConversationSignal[];
      next_page_token: string | null;
      previous_page_token: string | null;
      total_results: number;
    };
  }>("/conversation-catalog/signals/", { params });
  return {
    results: data.output?.items ?? [],
    next_page_token: data.output?.next_page_token ?? null,
    previous_page_token: data.output?.previous_page_token ?? null,
    total_results: data.output?.total_results ?? 0,
  };
}

async function createSignal(payload: SignalUpsertPayload): Promise<ConversationSignal> {
  const { data } = await apiClient.post<{ output: ConversationSignal }>(
    "/conversation-catalog/signals/create/",
    payload
  );
  return data.output;
}

async function updateSignal(
  key: string,
  payload: Omit<SignalUpsertPayload, "scope_type" | "scope_uuid">
): Promise<ConversationSignal> {
  const { data } = await apiClient.patch<{ output: ConversationSignal }>(
    `/conversation-catalog/signals/${key}/update/`,
    payload
  );
  return data.output;
}

async function deleteSignal(key: string): Promise<void> {
  await apiClient.delete(`/conversation-catalog/signals/${key}/delete/`);
}

async function setSignalStrategies(
  key: string,
  strategyKeys: string[]
): Promise<ConversationSignal> {
  const { data } = await apiClient.post<{ output: ConversationSignal }>(
    `/conversation-catalog/signals/${key}/strategies/`,
    { strategy_uuids: strategyKeys }
  );
  return data.output;
}

// ── Strategies ────────────────────────────────────────────────────────────────

async function listStrategies(params?: {
  search?: string;
  page_token?: string;
  max_results?: number;
  scope_type?: "stream" | "user";
  scope_uuid?: string;
  only_global?: boolean;
  include_global?: boolean;
}): Promise<ConversationStrategyListResponse> {
  const { data } = await apiClient.get<{
    output: {
      items: ConversationStrategy[];
      next_page_token: string | null;
      previous_page_token: string | null;
      total_results: number;
    };
  }>("/conversation-catalog/strategies/", { params });
  return {
    results: data.output?.items ?? [],
    next_page_token: data.output?.next_page_token ?? null,
    previous_page_token: data.output?.previous_page_token ?? null,
    total_results: data.output?.total_results ?? 0,
  };
}

async function createStrategy(payload: StrategyUpsertPayload): Promise<ConversationStrategy> {
  const { data } = await apiClient.post<{ output: ConversationStrategy }>(
    "/conversation-catalog/strategies/create/",
    payload
  );
  return data.output;
}

async function updateStrategy(
  key: string,
  payload: Pick<StrategyUpsertPayload, "label" | "description">
): Promise<ConversationStrategy> {
  const { data } = await apiClient.patch<{ output: ConversationStrategy }>(
    `/conversation-catalog/strategies/${key}/update/`,
    payload
  );
  return data.output;
}

async function deleteStrategy(key: string): Promise<void> {
  await apiClient.delete(`/conversation-catalog/strategies/${key}/delete/`);
}

export const signalsApi = {
  list: listSignals,
  create: createSignal,
  update: updateSignal,
  delete: deleteSignal,
  setStrategies: setSignalStrategies,
};

export const strategiesApi = {
  list: listStrategies,
  create: createStrategy,
  update: updateStrategy,
  delete: deleteStrategy,
};
